import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Play, RotateCcw, Crown, Sparkles, Trophy, Grid, Copy, Volume2, Mic, Pause, Wifi, ArrowLeft } from 'lucide-react';
import confetti from 'canvas-confetti';
import Background from '../components/Background';

// LocalStorage keys for Reliability
const STORAGE_KEYS = {
    PLAYER_CARD: 'bingo_player_card',
    PLAYER_MARKED: 'bingo_player_marked',
    PLAYER_ROOM: 'bingo_player_room',
    PLAYER_NAME: 'bingo_player_name'
};

const BingoGame = ({ onExit }) => {
    const [mode, setMode] = useState('home'); // home, moderator, player
    const [roomCode, setRoomCode] = useState('');
    const [playerName, setPlayerName] = useState('');
    const [gameState, setGameState] = useState({
        calledNumbers: [],
        players: [],
        currentNumber: null,
        gameActive: false,
        gamePaused: false,
        winners: []
    });
    const [bingoCard, setBingoCard] = useState([]);
    const [marked, setMarked] = useState(new Set());
    const [showConfetti, setShowConfetti] = useState(false);

    // PeerJS State
    const [peer, setPeer] = useState(null);
    const [connections, setConnections] = useState([]);

    // New features state
    const [joinError, setJoinError] = useState('');
    const [isJoining, setIsJoining] = useState(false);
    const [speechEnabled, setSpeechEnabled] = useState(true);
    const [connectionStatus, setConnectionStatus] = useState('disconnected'); // connected, disconnected, reconnecting

    // Define unique prefixes for handshake
    const APP_ID = 'bingo-game-v1';
    const getHostId = useCallback((code) => `${APP_ID}-host-${code}`, []);

    // Helper functions - memoized
    const getLetterForNumber = useCallback((num) => {
        if (num <= 15) return 'B';
        if (num <= 30) return 'I';
        if (num <= 45) return 'N';
        if (num <= 60) return 'G';
        return 'O';
    }, []);

    const speakNumber = useCallback((num) => {
        if (speechEnabled && 'speechSynthesis' in window) {
            window.speechSynthesis.cancel();
            const letter = getLetterForNumber(num);
            const text = `${letter} ... ${num}`;
            const msg = new SpeechSynthesisUtterance(text);
            msg.rate = 0.9;
            window.speechSynthesis.speak(msg);
        }
    }, [speechEnabled, getLetterForNumber]);

    // Fire confetti effect using canvas-confetti
    const fireConfetti = useCallback(() => {
        const duration = 3000;
        const end = Date.now() + duration;

        (function frame() {
            confetti({
                particleCount: 5,
                angle: 60,
                spread: 55,
                origin: { x: 0 },
                colors: ['#8B5CF6', '#EC4899', '#3B82F6']
            });
            confetti({
                particleCount: 5,
                angle: 120,
                spread: 55,
                origin: { x: 1 },
                colors: ['#8B5CF6', '#EC4899', '#3B82F6']
            });

            if (Date.now() < end) {
                requestAnimationFrame(frame);
            }
        }());
    }, []);

    // Save player state to localStorage for Reliability
    const savePlayerState = useCallback(() => {
        if (mode === 'player' && bingoCard.length > 0) {
            localStorage.setItem(STORAGE_KEYS.PLAYER_CARD, JSON.stringify(bingoCard));
            localStorage.setItem(STORAGE_KEYS.PLAYER_MARKED, JSON.stringify([...marked]));
            localStorage.setItem(STORAGE_KEYS.PLAYER_ROOM, roomCode);
            localStorage.setItem(STORAGE_KEYS.PLAYER_NAME, playerName);
        }
    }, [mode, bingoCard, marked, roomCode, playerName]);

    // Load player state from localStorage
    const loadPlayerState = useCallback(() => {
        try {
            const savedCard = localStorage.getItem(STORAGE_KEYS.PLAYER_CARD);
            const savedMarked = localStorage.getItem(STORAGE_KEYS.PLAYER_MARKED);
            const savedRoom = localStorage.getItem(STORAGE_KEYS.PLAYER_ROOM);
            const savedName = localStorage.getItem(STORAGE_KEYS.PLAYER_NAME);

            if (savedCard && savedRoom && savedName) {
                return {
                    card: JSON.parse(savedCard),
                    marked: new Set(JSON.parse(savedMarked || '[]')),
                    room: savedRoom,
                    name: savedName
                };
            }
        } catch (e) {
            console.error("Failed to load saved state", e);
        }
        return null;
    }, []);

    // Clear saved player state
    const clearPlayerState = useCallback(() => {
        Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
    }, []);

    // Auto-save player state on mark changes
    useEffect(() => {
        if (mode === 'player') {
            savePlayerState();
        }
    }, [mode, marked, savePlayerState]);

    // Initialize Peer for Moderator
    useEffect(() => {
        if (mode === 'moderator' && roomCode) {
            const hostId = getHostId(roomCode);
            import('peerjs').then(({ Peer }) => {
                const newPeer = new Peer(hostId, {
                    debug: 1,
                    config: {
                        'iceServers': [
                            { urls: 'stun:stun.l.google.com:19302' },
                            { urls: 'stun:stun1.l.google.com:19302' },
                            { urls: 'stun:stun2.l.google.com:19302' },
                            { urls: 'stun:stun3.l.google.com:19302' },
                            { urls: 'stun:stun4.l.google.com:19302' },
                        ]
                    }
                });

                newPeer.on('open', (id) => console.log('Moderator Peer ID:', id));

                newPeer.on('connection', (conn) => {
                    conn.on('open', () => {
                        setConnections(prev => {
                            if (!prev.find(c => c.peer === conn.peer)) {
                                return [...prev, { conn, status: 'connected', peerId: conn.peer }];
                            }
                            return prev;
                        });
                    });

                    conn.on('data', (data) => {
                        if (data.type === 'PLAYER_JOIN') {
                            setGameState(prev => {
                                const existing = prev.players.find(p => p.name === data.payload.name);

                                // Always send current game state to the joining/rejoining player
                                conn.send({
                                    type: 'GAME_UPDATE',
                                    payload: {
                                        calledNumbers: prev.calledNumbers,
                                        currentNumber: prev.currentNumber,
                                        gamePaused: prev.gamePaused
                                    }
                                });

                                if (!existing) {
                                    return {
                                        ...prev,
                                        players: [...prev.players, {
                                            name: data.payload.name,
                                            id: Date.now(),
                                            peerId: conn.peer,
                                            status: 'connected'
                                        }]
                                    };
                                } else {
                                    // Player Reconnection: Take over the existing slot with the new peerId
                                    return {
                                        ...prev,
                                        players: prev.players.map(p =>
                                            p.name === data.payload.name ? { ...p, status: 'connected', peerId: conn.peer } : p
                                        )
                                    };
                                }
                            });
                        }
                    });

                    conn.on('close', () => {
                        setGameState(prev => ({
                            ...prev,
                            players: prev.players.map(p =>
                                p.peerId === conn.peer ? { ...p, status: 'disconnected' } : p
                            )
                        }));
                        setConnections(prev => prev.filter(c => c.peerId !== conn.peer));
                    });
                });

                newPeer.on('error', (err) => {
                    if (err.type === 'unavailable-id') {
                        alert('Room code collision! Try a different code.');
                        setMode('home');
                    }
                });

                setPeer(newPeer);
            });
        }
    }, [mode, roomCode, getHostId]);

    // Global cleanup for Peer instance
    useEffect(() => {
        return () => {
            if (peer) {
                console.log('Global Peer Cleanup');
                peer.destroy();
            }
        };
    }, [peer]);

    // Handle URL Room Code & persistence
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlRoom = params.get('room');
        if (urlRoom) {
            setRoomCode(urlRoom);
        } else {
            const savedState = loadPlayerState();
            if (savedState) {
                setRoomCode(savedState.room);
                setPlayerName(savedState.name);
            }
        }
        // Remove the query param after picking it up to keep URLs clean
        if (urlRoom) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [loadPlayerState]);

    // Sync state to all connected players (Moderator only)
    useEffect(() => {
        if (mode === 'moderator' && connections.length > 0) {
            const payload = {
                type: 'GAME_UPDATE',
                payload: {
                    calledNumbers: gameState.calledNumbers,
                    currentNumber: gameState.currentNumber,
                    gamePaused: gameState.gamePaused
                }
            };
            connections.forEach(({ conn }) => {
                if (conn && conn.open) conn.send(payload);
            });
        }
    }, [gameState.calledNumbers, gameState.currentNumber, gameState.gamePaused, connections, mode]);

    // Generate random room code
    const generateRoomCode = useCallback(() => {
        return Math.floor(100000 + Math.random() * 900000).toString();
    }, []);

    // Start as moderator
    const startAsModerator = useCallback(() => {
        const code = generateRoomCode();
        setRoomCode(code);
        setMode('moderator');
        setGameState({
            calledNumbers: [],
            players: [],
            currentNumber: null,
            gameActive: false,
            gamePaused: false,
            winners: []
        });
    }, [generateRoomCode]);

    // Generate random bingo card - memoized
    const generateCard = useCallback(() => {
        const card = [];
        const ranges = [[1, 15], [16, 30], [31, 45], [46, 60], [61, 75]];
        ranges.forEach(([min, max]) => {
            const column = [];
            const used = new Set();
            while (column.length < 5) {
                const num = Math.floor(Math.random() * (max - min + 1)) + min;
                if (!used.has(num)) {
                    used.add(num);
                    column.push(num);
                }
            }
            card.push(column);
        });
        card[2][2] = 'FREE';
        return card;
    }, []);

    // Join as player logic with auto-reconnect
    const joinAsPlayer = useCallback((useExistingCard = false) => {
        if (!roomCode.trim() || !playerName.trim()) return;

        setJoinError('');
        setIsJoining(true);
        setConnectionStatus('reconnecting');

        import('peerjs').then(({ Peer }) => {
            const newPeer = new Peer({
                config: {
                    'iceServers': [
                        { urls: 'stun:stun.l.google.com:19302' },
                        { urls: 'stun:stun1.l.google.com:19302' },
                        { urls: 'stun:stun2.l.google.com:19302' },
                        { urls: 'stun:stun3.l.google.com:19302' },
                        { urls: 'stun:stun4.l.google.com:19302' },
                    ]
                }
            });

            newPeer.on('open', () => {
                const hostId = getHostId(roomCode.trim());
                const conn = newPeer.connect(hostId, { reliable: true });

                conn.on('open', () => {
                    setPeer(newPeer);
                    setConnectionStatus('connected');

                    conn.send({
                        type: 'PLAYER_JOIN',
                        payload: { name: playerName, roomCode: roomCode.trim() }
                    });

                    if (useExistingCard) {
                        const savedState = loadPlayerState();
                        if (savedState && savedState.room === roomCode.trim()) {
                            setBingoCard(savedState.card);
                            setMarked(savedState.marked);
                        } else {
                            setBingoCard(generateCard());
                            setMarked(new Set(['2-2']));
                        }
                    } else {
                        setBingoCard(generateCard());
                        setMarked(new Set(['2-2']));
                    }

                    setMode('player');
                    setIsJoining(false);
                });

                conn.on('data', (data) => {
                    if (data.type === 'GAME_UPDATE') {
                        setGameState(prev => ({
                            ...prev,
                            calledNumbers: data.payload.calledNumbers,
                            currentNumber: data.payload.currentNumber,
                            gamePaused: data.payload.gamePaused || false
                        }));
                    }
                });

                conn.on('close', () => {
                    setConnectionStatus('disconnected');
                    if (mode === 'player') {
                        setTimeout(() => {
                            console.log('Attempting auto-reconnect...');
                            joinAsPlayer(true);
                        }, 3000);
                    }
                });

                conn.on('error', () => setConnectionStatus('disconnected'));

                setTimeout(() => {
                    if (!conn.open) {
                        setJoinError('Could not find room. Ensure Moderator is online.');
                        setIsJoining(false);
                        setConnectionStatus('disconnected');
                        newPeer.destroy();
                    }
                }, 8000);
            });

            newPeer.on('error', () => {
                setJoinError('Connection failed. Please try again.');
                setIsJoining(false);
                setConnectionStatus('disconnected');
            });
        });
    }, [roomCode, playerName, getHostId, generateCard, loadPlayerState, mode]);

    // Rejoin with saved card for Reliability
    const rejoinGame = useCallback(() => {
        const savedState = loadPlayerState();
        if (savedState) {
            setRoomCode(savedState.room);
            setPlayerName(savedState.name);
            setTimeout(() => joinAsPlayer(true), 100);
        }
    }, [loadPlayerState, joinAsPlayer]);

    // Moderator actions
    const callNumber = useCallback(() => {
        if (gameState.gamePaused) return;
        const available = Array.from({ length: 75 }, (_, i) => i + 1).filter(i => !gameState.calledNumbers.includes(i));
        if (available.length > 0) {
            const num = available[Math.floor(Math.random() * available.length)];
            setGameState(prev => ({ ...prev, calledNumbers: [...prev.calledNumbers, num], currentNumber: num, gameActive: true }));
            speakNumber(num);
        }
    }, [gameState.calledNumbers, gameState.gamePaused, speakNumber]);

    const togglePause = useCallback(() => {
        setGameState(prev => ({ ...prev, gamePaused: !prev.gamePaused }));
    }, []);

    const resetGame = useCallback(() => {
        if (window.confirm('Reset the game? All progress will be lost.')) {
            setGameState({ calledNumbers: [], players: [], currentNumber: null, gameActive: false, gamePaused: false, winners: [] });
        }
    }, []);

    const markNumber = useCallback((col, row) => {
        const key = `${col}-${row}`;
        const num = bingoCard[col]?.[row];
        if (num !== 'FREE' && gameState.calledNumbers.includes(num)) {
            setMarked(prev => {
                const newMarked = new Set(prev);
                newMarked.has(key) ? newMarked.delete(key) : newMarked.add(key);
                return newMarked;
            });
        }
    }, [bingoCard, gameState.calledNumbers]);

    // Check for bingo - memoized for performance
    const hasBingo = useMemo(() => {
        if (mode !== 'player') return false;
        for (let i = 0; i < 5; i++) {
            if ([0, 1, 2, 3, 4].every(c => marked.has(`${c}-${i}`))) return true;
            if ([0, 1, 2, 3, 4].every(r => marked.has(`${i}-${r}`))) return true;
        }
        if ([0, 1, 2, 3, 4].every(i => marked.has(`${i}-${i}`))) return true;
        if ([0, 1, 2, 3, 4].every(i => marked.has(`${i}-${4 - i}`))) return true;
        return false;
    }, [mode, marked]);

    // Confetti trigger
    useEffect(() => {
        if (hasBingo && !showConfetti) {
            setShowConfetti(true);
            fireConfetti();
            setTimeout(() => setShowConfetti(false), 5000);
        }
    }, [hasBingo, showConfetti, fireConfetti]);

    const handleExit = useCallback(() => {
        if (mode === 'player') clearPlayerState();
        if (peer) peer.destroy();
        setPeer(null);
        setMode('home');
        if (mode === 'home' && onExit) onExit();
    }, [mode, peer, clearPlayerState, onExit]);

    // Home Screen
    if (mode === 'home') {
        const saved = loadPlayerState();
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <Background />
                <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-8 max-w-md w-full relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 animate-shimmer bg-[length:200%_auto]"></div>

                    <button
                        onClick={handleExit}
                        className="absolute top-4 left-4 p-2 text-white/50 hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>

                    <div className="text-center mb-10 mt-6">
                        <h1 className="text-6xl font-black bg-gradient-to-r from-white via-purple-200 to-white bg-clip-text text-transparent flex items-center justify-center gap-3">
                            <Sparkles className="w-12 h-12 text-purple-300 animate-pulse" /> BINGO <Sparkles className="w-12 h-12 text-purple-300 animate-pulse" />
                        </h1>
                        <p className="text-blue-200 mt-2 font-medium text-lg">Multiplayer Game Night</p>
                    </div>
                    <div className="space-y-6">
                        <button onClick={startAsModerator} className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white py-4 rounded-2xl font-bold text-lg shadow-lg flex items-center justify-center gap-3">
                            <Crown className="w-6 h-6" /> Start as Moderator
                        </button>
                        {saved && (
                            <button onClick={rejoinGame} className="w-full bg-emerald-600/80 hover:bg-emerald-500 text-white py-3 rounded-2xl font-bold flex items-center justify-center gap-3">
                                <RotateCcw className="w-5 h-5" /> Rejoin {saved.room} as {saved.name}
                            </button>
                        )}
                        <div className="relative py-2"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div><div className="relative flex justify-center text-sm"><span className="px-4 bg-transparent text-gray-400 font-medium">PLAYER JOIN</span></div></div>
                        <div className="space-y-4">
                            <div className="relative">
                                <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input type="text" placeholder="Your Name" value={playerName} onChange={(e) => setPlayerName(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border border-slate-700 rounded-xl text-white focus:border-purple-500 outline-none" />
                            </div>
                            <div className="space-y-2">
                                <div className="relative">
                                    <Grid className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input type="text" placeholder="Room Code" value={roomCode} onChange={(e) => setRoomCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border border-slate-700 rounded-xl text-white focus:border-pink-500 outline-none" inputMode="numeric" maxLength={6} />
                                </div>
                                {joinError && <p className="text-red-400 text-sm font-medium px-2 animate-pulse">{joinError}</p>}
                            </div>
                            <button onClick={() => joinAsPlayer(false)} disabled={!roomCode.trim() || !playerName.trim() || isJoining} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white py-4 rounded-2xl font-bold text-lg shadow-lg disabled:opacity-50 flex items-center justify-center gap-3">
                                {isJoining ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <Play className="w-6 h-6 fill-current" />}
                                {isJoining ? 'Joining...' : 'Join Game'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Moderator View
    if (mode === 'moderator') {
        const connectedCount = gameState.players.filter(p => p.status === 'connected').length;
        return (
            <div className="min-h-screen p-4 md:p-8 text-white">
                <Background />
                <div className="max-w-6xl mx-auto">
                    <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-4 md:p-6 mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <div className="p-2 md:p-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl md:rounded-2xl shadow-lg"><Crown className="w-6 h-6 md:w-8 md:h-8" /></div>
                            <div className="min-w-0">
                                <h2 className="text-lg md:text-2xl font-bold truncate">Moderator</h2>
                                <div className="flex items-center gap-2 text-white/60">
                                    <span className="text-[10px] md:text-sm">ROOM:</span><span className="font-mono text-sm md:text-xl font-bold text-purple-300 tracking-widest">{roomCode}</span>
                                    <button
                                        onClick={() => {
                                            const url = `${window.location.origin}${window.location.pathname}?room=${roomCode}`;
                                            navigator.clipboard.writeText(url);
                                            alert('Invite link copied!');
                                        }}
                                        className="flex items-center gap-1 px-2 py-1 bg-white/5 hover:bg-white/10 rounded text-[10px] transition border border-white/10"
                                    >
                                        <Copy className="w-2.5 h-2.5" /> Link
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 flex-wrap justify-center w-full md:w-auto">
                            <button onClick={() => setSpeechEnabled(!speechEnabled)} className={`p-2.5 rounded-xl border ${speechEnabled ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/20' : 'bg-slate-700/50 text-slate-400'}`}>{speechEnabled ? <Volume2 className="w-4 h-4 md:w-5 md:h-5" /> : <Mic className="w-4 h-4 md:w-5 md:h-5 opacity-50" />}</button>
                            <button onClick={togglePause} className={`px-4 md:px-6 py-2.5 rounded-xl font-semibold border flex items-center gap-2 text-sm ${gameState.gamePaused ? 'bg-green-500/20 text-green-300 border-green-500/20' : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20'}`}>{gameState.gamePaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}{gameState.gamePaused ? 'Resume' : 'Pause'}</button>
                            <button onClick={resetGame} className="px-4 md:px-6 py-2.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl font-semibold border border-red-500/20 flex items-center gap-2 text-sm"><RotateCcw className="w-4 h-4" /> Reset</button>
                            <button onClick={handleExit} className="px-4 py-2.5 bg-slate-700/50 hover:bg-slate-700/70 rounded-xl font-semibold border border-white/10 text-sm md:hidden">Exit</button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                        <div className="space-y-6">
                            <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-[32px] p-6 md:p-8 text-center relative overflow-hidden">
                                {gameState.gamePaused && <div className="absolute inset-0 bg-yellow-500/10 flex items-center justify-center z-20 backdrop-blur-[2px]"><span className="text-2xl font-bold text-yellow-400 animate-pulse">PAUSED</span></div>}
                                <h3 className="text-slate-400 font-medium uppercase tracking-widest mb-4 md:mb-6 text-[10px] md:text-sm">Current Number</h3>
                                <div className="relative mb-6 md:mb-10 h-40 md:h-64 flex items-center justify-center">
                                    {gameState.currentNumber ? (
                                        <div key={gameState.currentNumber} className="relative z-10 animate-pop">
                                            <div className="text-[7rem] md:text-[12rem] font-black leading-none bg-gradient-to-br from-white to-slate-400 bg-clip-text text-transparent">{gameState.currentNumber}</div>
                                            <div className="absolute -bottom-2 md:-bottom-4 left-1/2 -translate-x-1/2 px-4 md:px-6 py-1 md:py-2 bg-slate-800 rounded-full border border-white/10 text-base md:text-xl font-bold text-cyan-400">{getLetterForNumber(gameState.currentNumber)}</div>
                                        </div>
                                    ) : <div className="text-white/20 font-bold text-lg md:text-xl flex flex-col items-center gap-4"><div className="p-4 rounded-full bg-white/5"><Play className="w-10 h-10 md:w-12 md:h-12" /></div>Ready</div>}
                                </div>
                                <button onClick={callNumber} disabled={gameState.calledNumbers.length === 75 || gameState.gamePaused} className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 py-4 md:py-5 rounded-2xl text-xl md:text-2xl font-black disabled:opacity-50 transition-all flex items-center justify-center gap-3">
                                    {gameState.gameActive ? 'NEXT' : 'START'} <Play className="w-5 h-5 md:w-6 md:h-6 fill-white" />
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white/5 border border-white/10 rounded-2xl p-3 md:p-4 text-center"><div className="text-2xl md:text-3xl font-bold">{gameState.calledNumbers.length}</div><div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Called</div></div>
                                <div className="bg-white/5 border border-white/10 rounded-2xl p-3 md:p-4 text-center"><div className="text-2xl md:text-3xl font-bold">{75 - gameState.calledNumbers.length}</div><div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Left</div></div>
                            </div>
                        </div>
                        <div className="lg:col-span-2 space-y-6">
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-1 md:p-8">
                                <div className="flex items-center justify-between p-4 md:p-0 md:mb-6">
                                    <h3 className="text-white/80 font-bold flex items-center gap-3"><Grid className="w-5 h-5 text-purple-400" /> Board</h3>
                                </div>
                                <div className="space-y-2 md:space-y-4 p-2 md:p-0">
                                    {['B', 'I', 'N', 'G', 'O'].map((letter, idx) => (
                                        <div key={letter} className="flex gap-2 md:gap-3">
                                            <div className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0 bg-slate-800 rounded-lg flex items-center justify-center font-black text-slate-400 border border-white/5 text-xs md:text-sm">{letter}</div>
                                            <div className="flex-1 flex flex-wrap gap-1.5 md:gap-2">
                                                {Array.from({ length: 15 }, (_, i) => idx * 15 + 1 + i).map(num => (
                                                    <div key={num} className={`w-7 h-7 md:w-9 md:h-9 rounded-lg flex items-center justify-center text-[10px] md:text-xs font-bold transition-all ${gameState.currentNumber === num ? 'bg-yellow-500 text-white scale-110 shadow-lg' : gameState.calledNumbers.includes(num) ? 'bg-purple-600 text-white' : 'bg-slate-800/50 text-slate-600 border border-white/5'}`}>{num}</div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-6">
                                <h3 className="text-white/80 font-bold mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-pink-400" /> Players ({connectedCount}/{gameState.players.length} online)</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {gameState.players.map(player => (
                                        <div key={player.id} className={`bg-slate-800/50 border p-3 rounded-xl flex items-center gap-3 ${player.status === 'connected' ? 'border-green-500/30' : 'border-red-500/30 opacity-60'}`}>
                                            <div className="relative">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-xs font-bold">{player.name.charAt(0).toUpperCase()}</div>
                                                <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-slate-800 ${player.status === 'connected' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                            </div>
                                            <span className="truncate text-sm font-medium text-slate-300">{player.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Player View
    return (
        <div className="min-h-screen p-4 flex flex-col items-center">
            <Background />
            <div className="w-full max-w-md flex items-center justify-between mb-8 pt-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-lg">{playerName.charAt(0).toUpperCase()}</div>
                    <div>
                        <h2 className="text-white font-bold text-lg">{playerName}</h2>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-purple-200 bg-purple-500/20 px-2 py-0.5 rounded border border-purple-500/30">Room: {roomCode}</span>
                            <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${connectionStatus === 'connected' ? 'text-green-400 bg-green-500/20' : 'text-yellow-400 bg-yellow-500/20'}`}>
                                {connectionStatus === 'connected' ? <Wifi className="w-3 h-3" /> : <div className="w-3 h-3 border border-yellow-400 border-t-transparent rounded-full animate-spin" />}
                            </span>
                        </div>
                    </div>
                </div>
                <button onClick={handleExit} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition"><RotateCcw className="w-5 h-5" /></button>
            </div>
            {gameState.gamePaused && <div className="w-full max-w-md mb-4 bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-3 text-center"><span className="text-yellow-400 font-bold animate-pulse">⏸ Game Paused by Moderator</span></div>}
            {showConfetti && <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 animate-pop"><div className="bg-gradient-to-r from-yellow-300 via-orange-500 to-pink-500 p-1 rounded-3xl shadow-2xl"><div className="bg-slate-900 px-12 py-8 rounded-[22px] flex flex-col items-center text-center"><Trophy className="w-20 h-20 text-yellow-400 mb-4 animate-bounce" /><h2 className="text-5xl font-black text-white mb-2">BINGO!</h2><p className="text-slate-400">Winner!</p></div></div></div>}
            <div className="w-full max-w-md bg-white/10 backdrop-blur-2xl border border-white/20 rounded-[40px] p-4 md:p-6 shadow-2xl relative mb-12">
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-900 rounded-2xl p-3 md:p-4 shadow-[0_10px_40px_rgba(0,0,0,0.4)] border border-white/10 flex items-center gap-4 min-w-[160px] md:min-w-[200px] justify-center z-20">
                    <div className="text-[10px] md:text-sm text-white/40 uppercase font-black absolute -top-4 md:-top-5 bg-slate-900 px-3 py-0.5 rounded-full border border-white/10 tracking-[0.2em]">Latest</div>
                    {gameState.currentNumber ? <><span className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">{getLetterForNumber(gameState.currentNumber)}</span><div className="h-6 md:h-8 w-[1px] bg-white/10"></div><span className="text-3xl md:text-4xl font-black text-white">{gameState.currentNumber}</span></> : <span className="text-slate-500 font-medium animate-pulse">Waiting...</span>}
                </div>
                <div className="mt-8 md:mt-12">
                    <div className="grid grid-cols-5 gap-1 md:gap-2 mb-2">{['B', 'I', 'N', 'G', 'O'].map((l, i) => <div key={i} className="aspect-square flex items-center justify-center"><span className="text-xl md:text-2xl font-black text-white/20">{l}</span></div>)}</div>
                    <div className="grid grid-cols-5 gap-2 md:gap-3">
                        {Array.from({ length: 5 }).map((_, r) => bingoCard.map((col, c) => {
                            const num = col[r]; const key = `${c}-${r}`; const isM = marked.has(key); const isC = gameState.calledNumbers.includes(num);
                            return <button key={key} onClick={() => markNumber(c, r)} disabled={num === 'FREE'} className={`aspect-square rounded-2xl relative transition-all duration-300 ${num === 'FREE' ? 'bg-gradient-to-br from-yellow-400 to-orange-500 scale-95 shadow-inner' : isM ? 'bg-gradient-to-br from-purple-500 via-indigo-500 to-pink-600 text-white transform scale-95 shadow-lg ring-2 ring-white/20' : 'bg-white text-slate-800 shadow-sm hover:scale-105 active:scale-95 touch-manipulation'}`}>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    {num === 'FREE' ? <Crown className="w-5 h-5 md:w-6 md:h-6 text-white animate-pulse" /> : isM ? <div className="relative flex flex-col items-center justify-center"><span className="font-black text-base md:text-lg text-white drop-shadow-md leading-none">{num}</span><div className="w-1 h-1 bg-white rounded-full mt-1"></div></div> : <span className={`font-black text-lg md:text-2xl ${isC ? 'text-indigo-600' : 'text-slate-800'}`}>{num}</span>}
                                    {!isM && num !== 'FREE' && isC && <div className="absolute top-1 right-1 md:top-2 md:right-2 w-2 h-2 bg-red-500 rounded-full animate-ping"></div>}
                                </div>
                            </button>;
                        }))}
                    </div>
                </div>
            </div>
            {/* Recent Calls Row */}
            {gameState.calledNumbers.length > 0 && (
                <div className="w-full max-w-md bg-black/30 backdrop-blur-xl border border-white/5 rounded-3xl p-4 mb-8">
                    <h3 className="text-[10px] text-white/30 uppercase font-black tracking-widest mb-3 px-1">Recent History</h3>
                    <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar snap-x">
                        {[...gameState.calledNumbers].reverse().slice(0, 10).map((num, i) => (
                            <div key={i} className={`flex-shrink-0 w-12 h-12 rounded-xl flex flex-col items-center justify-center snap-center transition-all ${i === 0 ? 'bg-cyan-500 text-white shadow-lg scale-110' : 'bg-white/5 border border-white/5 text-white/60'}`}>
                                <span className="text-[8px] font-black opacity-50 leading-none mb-0.5">{getLetterForNumber(num)}</span>
                                <span className="text-sm font-bold leading-none">{num}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BingoGame;
